import { useState, useEffect, useRef, useCallback } from 'react';
import {
  readObjectFromLocalStorage,
  removeLocalStorage,
  writeObjectToLocalStorage,
} from '../../storage/localStorageAdapter';

function buildFilterStorageKey(pageKey: string, userId: string): string {
  return `filter_${pageKey}_${userId}`;
}

/**
 * Persists filter state in localStorage. State is scoped per-page-per-user so filters
 * never bleed between accounts.
 *
 * @param pageKey - Identifier for the page, e.g. 'fleet', 'maintenance'
 * @param userId - Scopes storage per user
 * @param defaultFilters - Returned when no saved state exists or on clear
 *
 * @returns Object with filters, setFilters, and clearFilters
 */
function usePersistedFilter<T extends object>(
  pageKey: string,
  userId: string,
  defaultFilters: T,
): { filters: T; setFilters: (filters: T) => void; clearFilters: () => void } {
  const storageKey = buildFilterStorageKey(pageKey, userId);
  const [filters, setFiltersState] = useState<T>(defaultFilters);
  const defaultFiltersRef = useRef(defaultFilters);
  defaultFiltersRef.current = defaultFilters;

  useEffect(() => {
    const stored = readObjectFromLocalStorage<T>(storageKey);
    const currentFilters = stored != null
      ? ({ ...defaultFiltersRef.current, ...stored } as T)
      : defaultFiltersRef.current;

    setFiltersState(currentFilters);
  }, [storageKey]);

  const setFilters = useCallback((newFilters: T) => {
    const saved = writeObjectToLocalStorage(storageKey, newFilters);
    if (saved) {
      setFiltersState(newFilters);
    } else {
      setFiltersState(defaultFiltersRef.current);
    }
  }, [storageKey]);

  const clearFilters = useCallback(() => {
    removeLocalStorage(storageKey);
    setFiltersState(defaultFiltersRef.current);
  }, [storageKey]);

  return { filters, setFilters, clearFilters };
}

export default usePersistedFilter;
